/**
 * Help constants (epic PI-SPEC-SW-E302, ER-1655, contract help 1).
 *
 * Single source of truth for the limits, the fixed action list, the link
 * allowlist and the screen keys that the SpecTree web Help panel, the
 * SpecTree API's Help inbox, the internal `toolbox-help` plugin skill and the
 * SpecTree Toolbox help engine share. Import these; never re-type the
 * numbers. Browser-safe: no runtime dependencies (the zod schemas that use
 * these live in `types.ts` and `api.ts`).
 */
/**
 * The marker the internal skill prints at the start of its one result line:
 * `SPECTREE_HELP_TURN {json}`. The JSON parses with `helpTurnMarkerSchema`
 * and is narrowed with `normalizeHelpTurnMarker`. The engine strips the line
 * from the streamed answer text (`stripHelpTurnMarker`), so it never reaches
 * the panel.
 */
export const HELP_TURN_MARKER = "SPECTREE_HELP_TURN";
/** Version of the `turn.json` file the help engine writes for the skill. */
export const HELP_TURN_INPUT_SCHEMA_VERSION = 1;
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
/** Most screenshots one question may carry, and one Help inbox report may keep. */
export const HELP_IMAGES_MAX = 3;
/** Largest screenshot, in decoded bytes (the same 10 MB cap as FogBugz screenshots). */
export const HELP_IMAGE_BYTES_MAX = 10 * 1024 * 1024;
/** Image types Help accepts for a screenshot. */
export const HELP_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
/** Longest screenshot file name. */
export const HELP_IMAGE_NAME_MAX = 200;
/** Longest question a person may type. */
export const HELP_QUESTION_MAX = 4000;
/** Longest note a person may add when sending a conversation to the Help inbox. */
export const HELP_NOTE_MAX = 2000;
/** Most earlier messages the engine hands the skill in `turn.json`. */
export const HELP_TURN_MESSAGES_MAX = 12;
/** Longest single message text in `turn.json` or a Help inbox report. */
export const HELP_MESSAGE_MAX = 20_000;
/** Most messages one Help inbox report may carry. */
export const HELP_INBOX_MESSAGES_MAX = 60;
/** Longest answer the engine reports in `turn-finished`. */
export const HELP_ANSWER_MAX = 20_000;
/** Most sources an answer keeps (extra sources are dropped, not refused). */
export const HELP_SOURCES_MAX = 8;
/** Most in-app links an answer keeps (extra links are dropped, not refused). */
export const HELP_LINKS_MAX = 4;
/** Most confirm buttons an answer keeps (extra buttons are dropped, not refused). */
export const HELP_ANSWER_ACTIONS_MAX = 3;
/** Longest label on a link or confirm button. */
export const HELP_LABEL_MAX = 80;
/** Longest source title. */
export const HELP_SOURCE_TITLE_MAX = 200;
/** Longest source reference (a guide section, screen name or file path). */
export const HELP_SOURCE_REF_MAX = 300;
/** Longest in-app link path. */
export const HELP_LINK_PATH_MAX = 512;
/** Longest page title in the context sent with a question. */
export const HELP_PAGE_TITLE_MAX = 200;
/** Most suggested questions the pack offers for one screen. */
export const HELP_SUGGESTIONS_PER_SCREEN_MAX = 6;
// ---------------------------------------------------------------------------
// Confirm buttons: the fixed action list (Resolved Decision 1)
// ---------------------------------------------------------------------------
/**
 * Every action a Help answer may offer as a confirm button. Claude never runs
 * an action: the skill can only name one from this list, the person clicks,
 * and the page performs it with an action it already has. Anything else the
 * skill names is dropped by `filterHelpActions`, never shown.
 *
 * - `retry-queue-entry`: queue a failed or stopped run again (`entryId`).
 * - `resume-queue`: resume a paused run queue.
 * - `update-repo-copies`: fetch the project's repository copies (`project`).
 * - `sign-in-claude`: open Claude Code's own sign-in.
 * - `sign-in-repos`: run the Git sign-in for the project's repositories (`project`).
 *
 * Adding an action is additive (help stays 1); removing or renaming one is a
 * breaking change to the help contract.
 */
export const HELP_ACTIONS = [
    "retry-queue-entry",
    "resume-queue",
    "update-repo-copies",
    "sign-in-claude",
    "sign-in-repos",
];
/** The plain default label of each confirm button, used when the skill's label is missing. */
export const HELP_ACTION_LABELS = {
    "retry-queue-entry": "Retry this run",
    "resume-queue": "Resume the queue",
    "update-repo-copies": "Update the project code",
    "sign-in-claude": "Sign in to Claude",
    "sign-in-repos": "Sign in to the code",
};
/** True when `value` is an action on the fixed list. */
export function isHelpActionId(value) {
    return typeof value === "string" && HELP_ACTIONS.includes(value);
}
// ---------------------------------------------------------------------------
// In-app links (Resolved Decision 1)
// ---------------------------------------------------------------------------
/**
 * The SpecTree page paths a Help link may open. A link path must equal one of
 * these or continue it with `/`, `?` or `#`. Every path is relative to the
 * SpecTree origin the Toolbox window already shows.
 */
export const HELP_LINK_PATH_PREFIXES = [
    "/toolbox",
    "/toolbox/setup",
    "/toolbox/formulator",
    "/toolbox/roles",
    "/grooming",
];
/**
 * True when `path` is an in-app path a Help link may open: it starts with a
 * single `/`, has no scheme, host, backslash, `..` segment (plain or
 * percent-encoded), whitespace or control character, and falls under one of
 * {@link HELP_LINK_PATH_PREFIXES}. Absolute URLs (`https://…`,
 * `//host/…`, `javascript:…`) are always refused.
 */
export function isHelpLinkPath(path) {
    if (typeof path !== "string" || path.length === 0 || path.length > HELP_LINK_PATH_MAX)
        return false;
    if (!path.startsWith("/") || path.startsWith("//"))
        return false;
    // Whitespace and control characters never belong in a link path.
    if (/[\s\p{Cc}]/u.test(path))
        return false;
    let decoded;
    try {
        decoded = decodeURIComponent(path);
    }
    catch {
        return false;
    }
    for (const candidate of [path, decoded]) {
        if (candidate.includes("\\") || candidate.includes("..") || candidate.startsWith("//"))
            return false;
    }
    const pathname = path.split(/[?#]/, 1)[0] ?? "";
    if (pathname.includes(":"))
        return false;
    return HELP_LINK_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}
// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------
/**
 * The Toolbox screens Help tells apart, for suggested questions and the
 * context sent with a question. `other` covers every page not listed.
 */
export const HELP_SCREEN_KEYS = [
    "home",
    "setup",
    "backlog",
    "session",
    "one-item",
    "queue",
    "project",
    "roles",
    "formulator",
    "other",
];
/** True when `value` is a known screen key. */
export function isHelpScreenKey(value) {
    return typeof value === "string" && HELP_SCREEN_KEYS.includes(value);
}
/**
 * The screen key of a SpecTree pathname (no query or hash needed; both are
 * ignored). Mirrors the web router: `/toolbox`, `/toolbox/setup`,
 * `/toolbox/formulator`, `/toolbox/roles`, `/grooming/queue`,
 * `/grooming/one`, and per project `/grooming/:project/{backlog,items/:id,
 * project/:tab,repos,session[/:batchId],agenda/:batchId,one}`.
 */
export function helpScreenKeyForPath(pathname) {
    let path = pathname.split(/[?#]/, 1)[0] ?? "";
    while (path.endsWith("/"))
        path = path.slice(0, -1);
    if (path === "")
        path = "/";
    const segments = path.split("/").filter((segment) => segment.length > 0);
    const [first, second, third] = segments;
    if (first === "toolbox") {
        if (second === undefined)
            return "home";
        if (second === "setup")
            return "setup";
        if (second === "formulator")
            return "formulator";
        if (second === "roles")
            return "roles";
        return "other";
    }
    if (first === "grooming") {
        if (second === undefined)
            return "backlog";
        if (second === "queue")
            return "queue";
        if (second === "one")
            return "one-item";
        if (third === undefined || third === "backlog" || third === "items")
            return "backlog";
        if (third === "project" || third === "repos")
            return "project";
        if (third === "session" || third === "agenda")
            return "session";
        if (third === "one")
            return "one-item";
        return "other";
    }
    return "other";
}
// ---------------------------------------------------------------------------
// Answers and runs
// ---------------------------------------------------------------------------
/**
 * How the skill judged its own answer:
 * - `answered`: grounded in the guide, the screens or the code;
 * - `not-sure`: Help could not find it and says so (the panel offers Send to Aaron);
 * - `off-topic`: the question is not about the Toolbox; a polite redirect;
 * - `out-of-date`: the pack describes an older SpecTree than the server runs.
 */
export const HELP_VERDICTS = ["answered", "not-sure", "off-topic", "out-of-date"];
/** Where an answer's source came from. `code` sources are never shown to non-developers as code. */
export const HELP_SOURCE_KINDS = ["guide", "screen", "code", "doc"];
/** Who wrote a message in a Help conversation. */
export const HELP_MESSAGE_ROLES = ["person", "help"];
/** How a help turn ended, as `turn-finished` reports it. */
export const HELP_TURN_OUTCOMES = ["answered", "failed", "stopped"];
/**
 * Phases of one help turn, in order, as the engine reports them in
 * `turn-progress` events. Pages show {@link HELP_RUN_PHASE_LABELS}, never the id.
 */
export const HELP_RUN_PHASES = ["starting", "looking-at-screen", "reading-guide", "checking-app", "writing"];
/** The plain phrase the panel shows for each phase. */
export const HELP_RUN_PHASE_LABELS = {
    starting: "Getting started",
    "looking-at-screen": "Looking at your screen",
    "reading-guide": "Reading the guide",
    "checking-app": "Checking how the app works",
    writing: "Writing the answer",
};
/**
 * Readiness of the help engine, as `help.getStatus()` and `status-changed`
 * report it. Every state other than `ready` shows the Setup guide and a plain
 * message instead of a chat (Help is needed most when Claude is the problem).
 */
export const HELP_STATUS_STATES = ["ready", "claude-missing", "signed-out", "usage-limit", "plugin-missing"];
//# sourceMappingURL=constants.js.map